import { DEFAULT_SMS_SENDER_NAME, deriveSmsSenderName, previewReminderShopName } from "@book4/utils";

export type ReminderNameNote = {
  /** One or two short sentences about the shop name in reminder texts. */
  message: string;
  /** Example reminder text from the same builder the reminder cron sends with. */
  example: string;
};

/**
 * Gentle setup-time check for the shop name. Runs the shared reminder builder with a typical
 * worst case (long service name, a first name, the shop phone) and returns a note when the shop
 * name would be shortened in reminder texts, or when it can't be used as the sender so texts come
 * from Book4. Returns null for a name that is fine (or empty). Never blocks saving.
 */
export function reminderNameNote(
  businessName: string | null | undefined,
  shopPhone?: string | null,
  options: { includeSenderFallback?: boolean } = {}
): ReminderNameNote | null {
  const name = businessName?.trim() ?? "";
  if (!name) return null;
  const check = previewReminderShopName({ businessName: name, shopPhone });
  const senderFallback = (options.includeSenderFallback ?? true) && deriveSmsSenderName(name) === null;
  const lines: string[] = [];
  if (check.dropped) {
    lines.push(
      "Your shop name is too long to fit in reminder texts, so they will leave it out and show the date, time and how to get in touch. Texts are kept to one message."
    );
  } else if (check.shortened) {
    lines.push(
      `Your shop name is long, so reminder texts will show it as "${check.shownAs}". Texts are kept to one message.`
    );
  }
  if (senderFallback) {
    lines.push(`Your shop name can't be used as the text sender, so reminder texts will come from ${DEFAULT_SMS_SENDER_NAME}.`);
  }
  if (lines.length === 0) return null;
  return { message: lines.join(" "), example: check.body };
}
